"use client";

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { ResponsiveCommandSelect } from "@/components/ui/responsive-select";
import { ResponsiveModal, useGlobalModalStack, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { TemplatePickerBody } from "@west-tennessee-consulting/email-templates/react";
import { useEmailTemplates, useTemplateFolders } from "@/lib/hooks/api/emails/email-templates";
import type { EmailTemplate } from "@/lib/hooks/api/emails/email-templates";
import { useEmailTemplateTags } from "@/lib/hooks/api/tags/tag-hooks";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { useEmailTemplateFavorites } from "@/lib/hooks/settings/use-email-template-favorites";
import { cn } from "@/lib/utils";
import { FileText, Star } from "lucide-react";
import type { TemplateSelectorProps } from "./template-selector/types";

/**
 * Hub's binding of the ONE canonical template picker
 * (EMAIL_TEMPLATES_CANONICAL_SYSTEM). The search/browser/preview UI is shared —
 * `TemplatePickerBody` from `@west-tennessee-consulting/email-templates/react`, so an enhancement lands
 * in `apps/next-email-client` too. What stays here is Hub-specific: the React
 * Query data hooks, the tag filter control, the favorites store, the modal shell,
 * and the desktop right-click menu (supplied to the shared rows via `wrapRow`).
 */
export function TemplateSelector({ onSelect, disabled = false, className }: TemplateSelectorProps) {
  const { searchValue, debouncedSearchValue, setSearchValue, clearSearch, isDebouncing } =
    useDebouncedSearch({
      delay: 300,
      initialValue: "",
    });
  const [selectedTag, setSelectedTag] = useState<string>("");
  const { openModal, closeModal } = useGlobalModalStack();
  const {
    favoriteIds,
    favoriteIdSet,
    toggleFavorite,
    pruneMissing,
    hydrated: favoritesHydrated,
  } = useEmailTemplateFavorites();

  const { data: allTemplates = [], isLoading: templatesLoading } = useEmailTemplates(undefined, {
    q: debouncedSearchValue.trim() || undefined,
    tags: selectedTag ? [selectedTag] : undefined,
  });
  const { data: folders = [], isLoading: foldersLoading } = useTemplateFolders();
  const { tags: availableTags = [], loading: tagsLoading } = useEmailTemplateTags();

  const isLoading = templatesLoading || foldersLoading;

  useEffect(() => {
    if (!favoritesHydrated) return;
    const q = debouncedSearchValue.trim();
    const tag = selectedTag;
    if (q !== "" || tag !== "" || templatesLoading || foldersLoading) return;
    if (allTemplates.length === 0) return;
    pruneMissing(new Set(allTemplates.map((t) => t.id)));
  }, [
    favoritesHydrated,
    debouncedSearchValue,
    selectedTag,
    templatesLoading,
    foldersLoading,
    allTemplates,
    pruneMissing,
  ]);

  const handleApplyTemplate = useCallback(
    (template: EmailTemplate) => {
      onSelect(template);
      closeModal(MODAL_IDS.messages.chooseTemplate);
      clearSearch();
      setSelectedTag("");
    },
    [onSelect, closeModal, clearSearch]
  );

  const resolveFavorite = useCallback((id: string) => favoriteIdSet.has(id), [favoriteIdSet]);

  /** Desktop right-click affordances around each shared row. */
  const wrapRow = useCallback(
    (template: EmailTemplate, row: ReactNode) => (
      <ContextMenu>
        <ContextMenuTrigger asChild>{row}</ContextMenuTrigger>
        {/* z-index-allow: the picker lives inside a Mantine ResponsiveModal (z-200); a portal-rendered context menu must outrank it */}
        <ContextMenuContent className="z-max">
          <ContextMenuItem onSelect={() => handleApplyTemplate(template)}>
            Use template
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={() => toggleFavorite(template.id)}>
            <Star
              className={cn("h-4 w-4", resolveFavorite(template.id) && "fill-amber-400 text-amber-500")}
            />
            {resolveFavorite(template.id) ? "Remove from Favorites" : "Add to Favorites"}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    ),
    [handleApplyTemplate, toggleFavorite, resolveFavorite]
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={disabled || isLoading}
        className={cn("justify-start", className)}
        onClick={() => openModal(MODAL_IDS.messages.chooseTemplate)}
      >
        <FileText className="h-4 w-4 mr-2" />
        {isLoading ? "Loading templates..." : "Choose Template"}
      </Button>

      <ResponsiveModal
        globalModalId={MODAL_IDS.messages.chooseTemplate}
        title="Select Email Template"
        description="Choose from available email templates"
        maxWidth="90vw"
        side="bottom"
        showCloseButton={true}
        className="flex flex-col h-full min-h-[70vh]"
      >
        <div className="flex-1 flex flex-col min-h-[70vh] overflow-x-hidden sm:overflow-x-auto overflow-y-hidden">
          <div className="flex-1 flex flex-col overflow-hidden w-full min-w-0 sm:min-w-[1000px]">
            <TemplatePickerBody<EmailTemplate>
              templates={allTemplates}
              folders={folders}
              isLoading={isLoading}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              isDebouncing={isDebouncing}
              appliedSearchTerm={debouncedSearchValue}
              selectedTag={selectedTag}
              tagFilter={
                <ResponsiveCommandSelect
                  value={selectedTag || "__all_tags__"}
                  onValueChange={(value) => setSelectedTag(value === "__all_tags__" ? "" : value)}
                  options={[
                    { value: "__all_tags__", label: "All tags", searchableText: "all tags" },
                    ...availableTags.map((tag: string) => ({
                      value: tag,
                      label: tag,
                      searchableText: tag.toLowerCase(),
                    })),
                  ]}
                  placeholder={tagsLoading ? "Loading tags..." : "Filter by tag"}
                  isSearching={false}
                />
              }
              favoriteIds={favoriteIds}
              isFavorite={resolveFavorite}
              onToggleFavorite={toggleFavorite}
              onApplyTemplate={handleApplyTemplate}
              wrapRow={wrapRow}
            />
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
